import React, { useState } from "react";
import { api } from "../utils/api";
import { PageHeader, Card } from "../components/ui/Card";
import { Button, SearchInput } from "../components/ui/Button";
import { EmptyState, LoadingRow } from "../components/ui/EmptyState";
import { colors, radius } from "../components/ui/tokens";
import { XIcon, TrashIcon } from "../components/ui/icons";

// Modération des messages — réservée au compte master (voir requireMasterAdmin côté backend,
// routes/adminMessages.ts). Le contenu est chiffré au repos ; ces écrans sont le seul endroit
// (avec les participants eux-mêmes) qui le déchiffre.
type AdminUserRow = { id: string; name: string; email: string; avatar_url?: string };
type ConversationRow = { id: string; type: string; other_user_name?: string; last_message?: string; last_message_at?: string };
type MessageRow = { id: string; content: string; type: string; created_at: string; user_id: string; user_name: string; is_edited?: boolean };

export default function Messages() {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null);
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [selectedConv, setSelectedConv] = useState<ConversationRow | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);

  const search = () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    api.get(`/admin/messages/users/search?q=${encodeURIComponent(query)}`)
      .then(data => setUsers(data?.users || []))
      .catch(() => setUsers([]))
      .finally(() => setSearching(false));
  };

  const openUser = (u: AdminUserRow) => {
    setSelectedUser(u);
    setSelectedConv(null);
    setMessages([]);
    api.get(`/admin/messages/users/${u.id}/conversations`).then(data => setConversations(data?.conversations || [])).catch(() => setConversations([]));
  };

  const openConversation = (c: ConversationRow) => {
    setSelectedConv(c);
    setLoadingThread(true);
    api.get(`/admin/messages/conversations/${c.id}`).then(data => setMessages(data?.messages || [])).catch(() => setMessages([])).finally(() => setLoadingThread(false));
  };

  const deleteMessage = (id: string) => {
    if (!window.confirm("Supprimer ce message ? Action irréversible.")) return;
    api.delete(`/admin/messages/${id}`).then(() => setMessages(prev => prev.filter(m => m.id !== id))).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Messages" subtitle="Accès réservé au compte principal, contenu chiffré au repos, déchiffré ici uniquement pour la modération" />

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <div onKeyDown={e => e.key === "Enter" && search()}>
          <SearchInput value={query} onChange={setQuery} placeholder="Rechercher un utilisateur par nom ou email..." width={360} />
        </div>
        <Button variant="primary" disabled={searching} onClick={search}>{searching ? "..." : "Rechercher"}</Button>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {users.length > 0 && !selectedUser && (
          <Card noPadding style={{ width: 320, overflow: "hidden" }}>
            {users.map(u => (
              <div key={u.id} onClick={() => openUser(u)} style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{u.name}</div>
                <div style={{ fontSize: 12, color: colors.textMuted }}>{u.email}</div>
              </div>
            ))}
          </Card>
        )}

        {selectedUser && (
          <Card noPadding style={{ width: 320, overflow: "hidden" }}>
            <div style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: colors.bg }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{selectedUser.name}</div>
                <div style={{ fontSize: 11.5, color: colors.textMuted }}>{selectedUser.email}</div>
              </div>
              <button onClick={() => { setSelectedUser(null); setUsers([]); }} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex" }}>
                <XIcon size={14} color={colors.textMuted} />
              </button>
            </div>
            {conversations.length === 0 ? (
              <div style={{ padding: 20 }}><EmptyState title="Aucune conversation" /></div>
            ) : conversations.map(c => (
              <div
                key={c.id} onClick={() => openConversation(c)}
                style={{ padding: "12px 14px", borderBottom: `1px solid ${colors.border}`, cursor: "pointer", backgroundColor: selectedConv?.id === c.id ? colors.bg : "white" }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600, color: colors.text }}>{c.other_user_name || (c.type === "direct" ? "Conversation" : c.type)}</div>
                <div style={{ fontSize: 12, color: colors.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.last_message || "—"}</div>
              </div>
            ))}
          </Card>
        )}

        {selectedConv && (
          <Card style={{ flex: 1, maxHeight: "70vh", overflowY: "auto" }}>
            {loadingThread ? (
              <LoadingRow />
            ) : messages.length === 0 ? (
              <EmptyState title="Aucun message" />
            ) : messages.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, padding: "10px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, color: colors.textFaint, marginBottom: 3 }}>
                    {m.user_name} · {new Date(m.created_at).toLocaleString("fr-FR")}{m.is_edited ? " · modifié" : ""}
                  </div>
                  <div style={{ fontSize: 13.5, color: colors.text, wordBreak: "break-word" }}>
                    {m.type === "text" || m.type === "date_confirmed" ? m.content : `[${m.type}] ${m.content}`}
                  </div>
                </div>
                <button onClick={() => deleteMessage(m.id)} style={{ flexShrink: 0, border: "none", background: colors.dangerSoft, borderRadius: radius.sm, cursor: "pointer", padding: 6, display: "flex" }}>
                  <TrashIcon size={13} color={colors.danger} />
                </button>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
